/**
 * ScatterPlotControl
 *
 * Scatter plot for displaying X/Y point data
 * Shows correlation patterns
 */

import React, { useMemo, memo } from 'react';
import type { ControlRendererProps } from '../ControlRenderer';
import { BaseChartComponent, type ChartDataPoint, extractChartData, getSeriesConfig } from './BaseChartComponent';

const ScatterPlotControl: React.FC<ControlRendererProps> = memo(({ control, value }) => {
  // CRITICAL: ONLY use control.config.staticData
  const staticData = (control.config?.staticData as ChartDataPoint[] | undefined) || [];
  const chartData = useMemo(() => {
    return staticData; // ONLY use configured data
  }, [staticData]);
  const seriesConfig = useMemo(() => getSeriesConfig(control), [control]);

  const scatterConfig = useMemo(() => {
    const config = control.config as Record<string, unknown> | undefined;
    return {
      xAxisKey: (config?.xAxisKey as string) || 'x',
      yAxisKey: (config?.yAxisKey as string) || 'y',
      dotSize: (config?.dotSize as number) || 6,
    };
  }, [control.config]);

  const renderScatter = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    const padding = 40;
    const width = 600;
    const height = (control.config?.height as number) || 400;
    const plotWidth = width - 2 * padding;
    const plotHeight = height - 2 * padding;

    // Extract X and Y values
    const xValues: number[] = [];
    const yValues: number[] = [];

    chartData.forEach((d) => {
      const x = (d as Record<string, unknown>)[scatterConfig.xAxisKey];
      const y = (d as Record<string, unknown>)[scatterConfig.yAxisKey];
      if (typeof x === 'number') xValues.push(x);
      if (typeof y === 'number') yValues.push(y);
    });

    const xMin = Math.min(...xValues, 0);
    const xMax = Math.max(...xValues, 1);
    const yMin = Math.min(...yValues, 0);
    const yMax = Math.max(...yValues, 1);

    const xRange = xMax - xMin || 1;
    const yRange = yMax - yMin || 1;

    const xScale = plotWidth / xRange;
    const yScale = plotHeight / yRange;

    return (
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: '100%' }}>
        {/* Grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const y = height - padding - ratio * plotHeight;
          return (
            <line
              key={`h-grid-${ratio}`}
              x1={padding}
              y1={y}
              x2={width - padding}
              y2={y}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          );
        })}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio) => {
          const x = padding + ratio * plotWidth;
          return (
            <line
              key={`v-grid-${ratio}`}
              x1={x}
              y1={padding}
              x2={x}
              y2={height - padding}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          );
        })}

        {/* Axes */}
        <line x1={padding} y1={padding} x2={padding} y2={height - padding} stroke="#000" />
        <line x1={padding} y1={height - padding} x2={width - padding} y2={height - padding} stroke="#000" />

        {/* Points */}
        {chartData.map((d, index) => {
          const x = (d as Record<string, unknown>)[scatterConfig.xAxisKey];
          const y = (d as Record<string, unknown>)[scatterConfig.yAxisKey];

          if (typeof x !== 'number' || typeof y !== 'number') return null;

          const pointX = padding + (x - xMin) * xScale;
          const pointY = height - padding - (y - yMin) * yScale;

          const series = seriesConfig.length > 0 ? seriesConfig[0] : null;
          const color = series?.color || '#8884d8';

          return (
            <circle
              key={`point-${index}`}
              cx={pointX}
              cy={pointY}
              r={scatterConfig.dotSize}
              fill={color}
              opacity="0.8"
            />
          );
        })}

        {/* X Axis labels */}
        {[0, 0.5, 1].map((ratio) => {
          const x = padding + ratio * plotWidth;
          const value = Math.round(xMin + ratio * xRange);
          return (
            <text
              key={`x-label-${ratio}`}
              x={x}
              y={height - padding + 20}
              textAnchor="middle"
              fontSize="12"
              fill="#6b7280"
            >
              {value}
            </text>
          );
        })}

        {/* Y Axis labels */}
        {[0, 0.5, 1].map((ratio) => {
          const y = height - padding - ratio * plotHeight;
          const value = Math.round(yMin + ratio * yRange);
          return (
            <text
              key={`y-label-${ratio}`}
              x={padding - 10}
              y={y}
              textAnchor="end"
              fontSize="12"
              fill="#6b7280"
            >
              {value}
            </text>
          );
        })}
      </svg>
    );
  }, [chartData, scatterConfig, control.config]);

  return (
    <BaseChartComponent control={control} value={value} chartType="scatter-plot">
      <div style={{ width: '100%', height: '100%' }}>{renderScatter}</div>
    </BaseChartComponent>
  );
});

ScatterPlotControl.displayName = 'ScatterPlotControl';
export default ScatterPlotControl;
